import { FormikErrors, FormikTouched } from 'formik'
import axios from 'axios'
import { getApiErrorData, getErrorMessage, showError } from '_metronic/helpers/AlertHelper'

type FormikErros<V> = {
  setErrors: (erros: FormikErrors<V>) => void
  setTouched: (tocados: FormikTouched<V>, validar?: boolean) => unknown
}

/**
 * Leva os erros por campo do 422 (`data.errors.<campo>`, com ou sem `message`) para o formik.
 * Copia só os campos que existem no formulário. Devolve `true` quando aplicou algum; quando
 * devolve `false`, quem chama mostra o erro de outro jeito (caixa no topo ou `showError`), para
 * nenhum erro ficar sem aparecer.
 */
export const aplicaErrosDoServidor = <V>(
  error: unknown,
  formik: FormikErros<V>,
  camposDoForm: readonly string[],
  /** Campo do backend → campo do formulário, quando os nomes diferem (ex.: `idEmpresa` → `empresa`). */
  mapa: Record<string, string> = {}
): boolean => {
  const erros = getApiErrorData(error)?.errors
  if (!erros) return false

  const doFormulario: Record<string, string> = {}
  for (const campoServidor of Object.keys(erros)) {
    const campo = mapa[campoServidor] ?? campoServidor
    if (!camposDoForm.includes(campo)) continue
    const mensagem = erros[campoServidor]
    const texto = Array.isArray(mensagem) ? mensagem[0] : mensagem
    if (texto) doFormulario[campo] = texto
  }

  const campos = Object.keys(doFormulario)
  if (!campos.length) return false

  formik.setErrors(doFormulario as FormikErrors<V>)
  formik.setTouched(
    Object.fromEntries(campos.map((campo) => [campo, true])) as FormikTouched<V>,
    false
  )
  return true
}

/** Erros por campo que NÃO estão no formulário, já como texto, para a caixa no topo do modal. */
export const errosForaDoFormulario = (
  error: unknown,
  camposDoForm: readonly string[],
  mapa: Record<string, string> = {}
): string[] => {
  const erros = getApiErrorData(error)?.errors
  if (!erros) return []
  return Object.entries(erros)
    .filter(([campo]) => !camposDoForm.includes(mapa[campo] ?? campo))
    .flatMap(([, mensagem]) => (Array.isArray(mensagem) ? mensagem : [mensagem]))
    .filter(Boolean)
}

/**
 * Tratamento de erro dos modais da gestão de dispositivos, para nenhum erro ficar sem aparecer:
 * - sem resposta (rede), 401, 403 ou 5xx → `showError` (alerta padrão do app);
 * - erro por campo do formulário → sob o campo (`aplicaErrosDoServidor`);
 * - erro por campo que não está no formulário, ou mensagem de regra (409, 400, 422) → caixa no
 *   topo do modal (`definirErroGeral`), com o modal aberto para o usuário corrigir.
 */
export const trataErroDoModal = <V>(
  error: unknown,
  formik: FormikErros<V>,
  camposDoForm: readonly string[],
  definirErroGeral: (mensagens: string[]) => void,
  mapa: Record<string, string> = {}
) => {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined
  if (status === undefined || status === 401 || status === 403 || status >= 500) {
    showError(error)
    return
  }
  const aplicou = aplicaErrosDoServidor(error, formik, camposDoForm, mapa)
  const fora = errosForaDoFormulario(error, camposDoForm, mapa)
  if (fora.length) {
    definirErroGeral(fora)
    return
  }
  if (aplicou) return
  const mensagem = getApiErrorData(error)?.message ?? getErrorMessage(error)
  if (mensagem) definirErroGeral([mensagem])
  else showError(error)
}
